import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { HxAlert } from '../alert/alert';
import { HxButton } from '../button/button';
import { HxIcon } from '../icon/icon';
import { HxTooltipDirective } from '../tooltip/tooltip';
import { DEFAULT_JSON_VIEWER_CONFIG, JsonViewerConfig, JsonViewerMode, JsonViewerNode } from './json-viewer.types';

/**
 * HxJsonViewer - Interactive JSON tree viewer component
 *
 * Features:
 *
 * - Collapsible tree structure
 * - Syntax highlighting
 * - Automatic parsing of JSON strings within valueString properties
 * - Expand/collapse all functionality
 * - Configurable indentation and behavior
 * - Error display for invalid JSON
 *
 * @example
 *   ```html
 *   <hx-json-viewer [json]="myJsonObject" />
 *
 *   <hx-json-viewer
 *     [json]="jsonString"
 *     [config]="{ defaultCollapsed: true }"
 *   />
 *   ```;
 */
@Component({
  selector: 'hx-json-viewer',
  standalone: true,
  imports: [CommonModule, HxAlert, HxButton, HxIcon, HxTooltipDirective],
  templateUrl: './json-viewer.html',
  styleUrls: ['./json-viewer.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-json-viewer-host',
  },
})
export class HxJsonViewer {
  /** JSON data to display (string or object) */
  readonly json = input.required<string | object>();

  /** Configuration options */
  readonly config = input<JsonViewerConfig>({});

  /** Placeholder text for the editor textarea */
  readonly placeholder = input<string>('Enter JSON...');

  /** Event emitted when a node is toggled */
  readonly nodeToggled = output<{ node: JsonViewerNode; collapsed: boolean }>();

  /** Event emitted when JSON parsing fails */
  readonly parseError = output<{ error: string; input: string }>();

  /** Emits when editor content changes (edit mode only) */
  readonly contentChange = output<string>();

  /** Emits when JSON is formatted successfully (edit mode only) */
  readonly formatted = output<void>();

  /** Emits when formatting fails due to invalid JSON (edit mode only) */
  readonly formatError = output<string>();

  /** View mode: 'tree' for interactive tree, 'raw' for plain text, 'edit' for editor */
  readonly viewMode = signal<JsonViewerMode>('tree');

  /** Whether the copy notification is showing */
  readonly copied = signal(false);

  /** Whether the toolbar should be shown */
  readonly showToolbar = computed(() => this.mergedConfig().showToolbar ?? false);

  /** Whether editor mode is enabled */
  readonly editable = computed(() => this.mergedConfig().editable ?? false);

  /** Whether to show footer (line/char counts in edit mode) */
  readonly showFooter = computed(() => this.mergedConfig().showFooter ?? false);

  /** Editor content for edit mode (kept in sync with json input) */
  readonly editorContent = signal<string>('');

  /** Number of characters in the editor content */
  readonly characterCount = computed(() => this.editorContent().length);

  /** Number of lines in the editor content */
  readonly lineCount = computed(() => {
    const c = this.editorContent();
    return c ? c.split('\n').length : 0;
  });

  /** Trigger for re-rendering tree (incremented on toggle) */
  private renderTrigger = signal(0);

  /** Cached tree nodes - stored separately to persist collapsed state */
  private cachedNodes = signal<JsonViewerNode[]>([]);

  /** Cached error message */
  private cachedError = signal<string | null>(null);

  /** Merged configuration with defaults */
  private mergedConfig = computed(() => ({
    ...DEFAULT_JSON_VIEWER_CONFIG,
    ...this.config(),
  }));

  /** Parse error message (null if valid JSON) */
  readonly errorMessage = computed(() => this.cachedError());

  /** Root nodes of the tree */
  readonly rootNodes = computed(() => {
    // Subscribe to renderTrigger to re-render on toggle
    this.renderTrigger();
    return this.cachedNodes();
  });

  constructor() {
    // Effect to rebuild tree when JSON input changes
    effect(() => {
      const jsonInput = this.json();
      this.buildTreeFromInput(jsonInput);
      // Sync editor content from input
      if (typeof jsonInput === 'string') {
        this.editorContent.set(jsonInput);
      } else {
        this.editorContent.set(JSON.stringify(jsonInput, null, 2));
      }
    });

    // Effect to set initial view mode from config
    effect(() => {
      const mode = this.mergedConfig().initialViewMode;
      if (mode) {
        this.viewMode.set(mode);
      }
    });
  }

  /** Build tree from JSON input and update cached state */
  private buildTreeFromInput(jsonInput: string | object): void {
    let parsed: unknown;

    if (typeof jsonInput === 'string') {
      try {
        parsed = JSON.parse(jsonInput);
        this.cachedError.set(null);
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Invalid JSON';
        this.cachedError.set(errorMsg);
        this.parseError.emit({ error: errorMsg, input: jsonInput });
        this.cachedNodes.set([]);
        return;
      }
    } else {
      parsed = jsonInput;
      this.cachedError.set(null);
    }

    const nodes = this.buildTree(parsed, 'root');
    this.cachedNodes.set(nodes);
  }

  /** Indentation size per level */
  readonly indentSize = computed(() => this.mergedConfig().indentSize ?? 16);

  /** Whether to show line numbers */
  readonly showLineNumbers = computed(() => this.mergedConfig().showLineNumbers ?? false);

  /** Toggle node collapsed state */
  toggleNode(node: JsonViewerNode): void {
    node.collapsed = !node.collapsed;
    this.nodeToggled.emit({ node, collapsed: node.collapsed });
    // Force re-render by incrementing trigger
    this.renderTrigger.update((v) => v + 1);
  }

  /** Expand all collapsible nodes */
  expandAll(): void {
    const nodes = this.cachedNodes();
    this.setCollapsedRecursive(nodes, false);
    // Force signal update by setting a new array reference
    this.cachedNodes.set([...nodes]);
    this.renderTrigger.update((v) => v + 1);
  }

  /** Collapse all collapsible nodes */
  collapseAll(): void {
    const nodes = this.cachedNodes();
    this.setCollapsedRecursive(nodes, true);
    // Force signal update by setting a new array reference
    this.cachedNodes.set([...nodes]);
    this.renderTrigger.update((v) => v + 1);
  }

  /** Check if a node is expandable (object, array, or json-string) */
  isExpandable(node: JsonViewerNode): boolean {
    return node.type === 'object' || node.type === 'array' || node.type === 'json-string';
  }

  /** Toggle between tree and raw view modes */
  toggleViewMode(): void {
    this.viewMode.update((mode) => (mode === 'tree' ? 'raw' : 'tree'));
  }

  /** Set view mode directly */
  setViewMode(mode: JsonViewerMode): void {
    this.viewMode.set(mode);
  }

  /** Copy the JSON data to clipboard */
  async copyToClipboard(): Promise<void> {
    try {
      const text = this.viewMode() === 'edit' ? this.editorContent() : this.getJsonText();
      await navigator.clipboard.writeText(text);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 3000);
    } catch (err) {
      console.error('Failed to copy JSON to clipboard:', err);
    }
  }

  /** Handle textarea input in edit mode */
  onEditorInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.editorContent.set(textarea.value);
    this.contentChange.emit(textarea.value);
  }

  /** Format JSON in the editor */
  onFormat(): void {
    const raw = this.editorContent();
    if (!raw || !raw.trim()) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const formatted = JSON.stringify(parsed, null, 2);
      this.editorContent.set(formatted);
      this.contentChange.emit(formatted);
      this.formatted.emit();
    } catch {
      this.formatError.emit('Invalid JSON: Cannot format');
    }
  }

  /** Get the current JSON text */
  private getJsonText(): string {
    const jsonInput = this.json();
    return typeof jsonInput === 'string' ? jsonInput : JSON.stringify(jsonInput, null, 2);
  }

  /** Get raw JSON string for display in raw mode */
  getRawJsonString(): string {
    const jsonInput = this.json();
    if (typeof jsonInput === 'string') {
      try {
        return JSON.stringify(JSON.parse(jsonInput), null, 2);
      } catch {
        return jsonInput;
      }
    }
    return JSON.stringify(jsonInput, null, 2);
  }

  /** Recursively set collapsed state */
  private setCollapsedRecursive(nodes: JsonViewerNode[], collapsed: boolean): void {
    for (const node of nodes) {
      if (this.isExpandable(node)) {
        node.collapsed = collapsed;
      }
      if (node.children) {
        this.setCollapsedRecursive(node.children, collapsed);
      }
    }
  }

  /** Build tree structure from value */
  private buildTree(value: unknown, key: string): JsonViewerNode[] {
    const node = this.createNode(key, value, 0);
    return [node];
  }

  /** Create a node from a value */
  private createNode(key: string, value: unknown, depth: number): JsonViewerNode {
    const config = this.mergedConfig();
    const maxDepth = config.maxAutoExpandDepth ?? -1;
    const shouldCollapse = config.defaultCollapsed || (maxDepth >= 0 && depth > maxDepth);

    if (value === null) {
      return { key, value: null, type: 'null', collapsed: false };
    }

    if (Array.isArray(value)) {
      const children = value.map((item, index) => this.createNode(String(index), item, depth + 1));
      return {
        key,
        value,
        type: 'array',
        collapsed: shouldCollapse,
        children,
      };
    }

    if (typeof value === 'object') {
      const children = Object.entries(value as Record<string, unknown>).map(([k, v]) =>
        this.createNode(k, v, depth + 1)
      );
      return {
        key,
        value,
        type: 'object',
        collapsed: shouldCollapse,
        children,
      };
    }

    if (typeof value === 'string') {
      // Check if this key should have its value parsed as JSON
      const jsonStringKeys = config.jsonStringKeys ?? ['valueString'];
      if (jsonStringKeys.includes(key)) {
        const parsed = this.tryParseJson(value);
        if (parsed !== null && typeof parsed === 'object') {
          const children = Object.entries(parsed as Record<string, unknown>).map(([k, v]) =>
            this.createNode(k, v, depth + 1)
          );
          return {
            key,
            value,
            type: 'json-string',
            collapsed: false, // Always expand JSON strings for visibility
            children,
            parsedJson: parsed,
          };
        }
      }
      return { key, value, type: 'string', collapsed: false };
    }

    if (typeof value === 'number') {
      return { key, value, type: 'number', collapsed: false };
    }

    if (typeof value === 'boolean') {
      return { key, value, type: 'boolean', collapsed: false };
    }

    return { key, value: String(value), type: 'string', collapsed: false };
  }

  /** Try to parse a string as JSON */
  private tryParseJson(str: string): unknown {
    try {
      const parsed = JSON.parse(str);
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }
}
